import React from "react";
import { useCompleteProfile } from "@/context/CompleteProfileContext";
import CompleteProfileFooter from "../CompleteProfileFooter";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FILTER_OPTIONS, FILTER_LABELS } from "@/types/filter-options";
import { Sparkles } from "lucide-react";

const AppearanceStep = () => {
  const { data, updateData, autoSaveField, nextStep, saving } = useCompleteProfile();

  const handleUpdateData = (field: string, value: string) => {
    updateData({ [field]: value });
    autoSaveField(field, value); // Auto-save immediately on selection
  };

  // All fields required
  const canProceed = Boolean(data.height && data.body_type && data.hair_color && data.eye_color);

  const getSelectClass = (value: string | null | undefined) => 
    `bg-white/5 text-white rounded-xl h-12 ${
      value 
        ? 'border-2 border-luxury-pinkBright' 
        : 'border border-white/20 hover:border-white/40'
    }`;
  const contentClass = "bg-black border border-white/20 text-white rounded-xl shadow-xl z-50";
  const itemClass = "text-white hover:bg-white/10 hover:text-white focus:bg-white/10 focus:text-white";

  return (
    <CompleteProfileFooter
      buttonText="Continuar"
      onClick={nextStep}
      disabled={!canProceed}
      isLoading={saving}
    >
      <div className="space-y-6">
        {/* Header */}
        <div className="text-center space-y-2">
          <div className="w-16 h-16 rounded-full bg-luxury-pinkBright/20 flex items-center justify-center mx-auto mb-4">
            <Sparkles className="w-8 h-8 text-luxury-pinkBright" />
          </div>
          <h2 className="text-2xl font-bold text-white">Aparência Física</h2>
          <p className="text-white/60 text-sm">
            Ajuda as pessoas a te encontrarem nos filtros
          </p>
        </div>

        {/* Form Fields */}
        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="text-white">Altura <span className="text-luxury-pinkBright">*</span></Label>
            <Select value={data.height || ''} onValueChange={(value) => handleUpdateData('height', value)}>
              <SelectTrigger className={getSelectClass(data.height)}>
                <SelectValue placeholder="Selecione sua altura" />
              </SelectTrigger>
              <SelectContent className={contentClass}>
                {FILTER_OPTIONS.height.map(option => (
                  <SelectItem key={option} value={option} className={itemClass}>
                    {FILTER_LABELS.height[option as keyof typeof FILTER_LABELS.height]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className="text-white">Tipo Físico <span className="text-luxury-pinkBright">*</span></Label>
            <Select value={data.body_type || ''} onValueChange={(value) => handleUpdateData('body_type', value)}>
              <SelectTrigger className={getSelectClass(data.body_type)}>
                <SelectValue placeholder="Selecione seu tipo" />
              </SelectTrigger>
              <SelectContent className={contentClass}>
                {FILTER_OPTIONS.bodyType.map(option => (
                  <SelectItem key={option} value={option} className={itemClass}>
                    {FILTER_LABELS.bodyType[option as keyof typeof FILTER_LABELS.bodyType]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-white">Cor do Cabelo <span className="text-luxury-pinkBright">*</span></Label>
              <Select value={data.hair_color || ''} onValueChange={(value) => handleUpdateData('hair_color', value)}>
                <SelectTrigger className={getSelectClass(data.hair_color)}>
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent className={contentClass}>
                  {FILTER_OPTIONS.hairColor.map(option => (
                    <SelectItem key={option} value={option} className={itemClass}>
                      {FILTER_LABELS.hairColor[option as keyof typeof FILTER_LABELS.hairColor]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-white">Cor dos Olhos <span className="text-luxury-pinkBright">*</span></Label>
              <Select value={data.eye_color || ''} onValueChange={(value) => handleUpdateData('eye_color', value)}>
                <SelectTrigger className={getSelectClass(data.eye_color)}>
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent className={contentClass}>
                  {FILTER_OPTIONS.eyeColor.map(option => (
                    <SelectItem key={option} value={option} className={itemClass}>
                      {FILTER_LABELS.eyeColor[option as keyof typeof FILTER_LABELS.eyeColor]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Encouragement */}
        <div className="bg-white/5 border border-white/10 rounded-xl p-4 mt-6">
          <p className="text-sm text-white/70 text-center">
            ✨ Preencher esses campos aumenta sua visibilidade nos filtros de busca
          </p>
        </div>
      </div>
    </CompleteProfileFooter>
  );
};

export default AppearanceStep;
